import { useState } from 'react'
import axiosInstance from 'libs/shared/data-access/src/lib/clients/axiosClient/axiosConfig'
import { getApiBaseUrl } from 'libs/shared/data-access/src/lib/utils/getApiBaseUrl'

type RateVideoCallParams = {
  room_id: string
  rating: number
  user_id?: string
  comment?: string
}

type RateVideoCallResponse = {
  success: boolean
  message?: string
}

const useRateVideoCall = () => {
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<unknown>(null)
  const [response, setResponse] = useState<RateVideoCallResponse | null>(null)

  const rateVideoCall = async (params: RateVideoCallParams) => {
    if (params.rating < 1 || params.rating > 5) {
      const ratingError = new Error('Rating must be between 1 and 5')
      console.error(ratingError)
      setError(ratingError)
      return
    }

    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
    if (!uuidRegex.test(params.room_id)) {
      console.warn(
        'Warning: roomId may not be a valid UUID format:',
        params.room_id,
      )
    }

    if (params.user_id && !uuidRegex.test(params.user_id)) {
      console.warn('Warning: Invalid user_id format, removing from request')
      delete params.user_id
    }

    setLoading(true)
    setError(null)

    try {
      const response = await axiosInstance.post(
        'video/rate_videocall',
        params,
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        },
      )

      setResponse({
        success: true,
        message: response.data?.message || 'Rating submitted successfully',
      })

      return response.data
    } catch (err: any) {
      console.error('Error calling API:', err)

      if (err.response?.status === 403) {
        const csrfError = new Error(
          'CSRF verification failed. Please reload the page and try again.',
        )
        setError(csrfError)
        setResponse({
          success: false,
          message:
            'CSRF verification failed. Please reload the page and try again.',
        })
      } else {
        setError(err)
        setResponse({
          success: false,
          message:
            err instanceof Error ? err.message : 'Failed to submit rating',
        })
      }

      throw err
    } finally {
      setLoading(false)
    }
  }

  return { rateVideoCall, loading, error, response }
}

export default useRateVideoCall
